import { AppReleaseConfig, AppUpdateMode, getAppConfig } from '../config/app.config';
import {
  AppBuildManifest,
  CURRENT_APP_BUILD,
  isAppBuildManifest
} from '../config/build.config';
import { appBootstrapService } from './AppBootstrapService';

export interface AppUpdateDecision {
  available: boolean;
  mode: AppUpdateMode;
  currentBuild: number;
  availableBuild: number;
  deploymentReady: boolean;
}

export interface AppUpdateEventDetail extends AppUpdateDecision {
  message: string;
  manifest: AppBuildManifest;
}

export const evaluateAppUpdate = (
  currentBuild: number,
  manifestBuild: number,
  release: AppReleaseConfig
): AppUpdateDecision => {
  const configuredTarget = Number.isSafeInteger(release.latestBuild) && release.latestBuild > 0
    ? release.latestBuild
    : manifestBuild;
  const requiredTarget = release.minimumBuild > 0
    ? Math.max(configuredTarget, release.minimumBuild)
    : configuredTarget;
  const deploymentReady = manifestBuild >= requiredTarget;
  const available = release.updateMode !== 'disabled'
    && deploymentReady
    && currentBuild > 0
    && requiredTarget > currentBuild;
  const belowMinimum = release.minimumBuild > 0 && currentBuild < release.minimumBuild;

  return {
    available,
    mode: available && (release.updateMode === 'required' || belowMinimum)
      ? 'required'
      : release.updateMode,
    currentBuild,
    availableBuild: requiredTarget,
    deploymentReady
  };
};

export class AppUpdateService {
  private static instance: AppUpdateService;
  private registration: ServiceWorkerRegistration | null = null;
  private interval: number | null = null;
  private started = false;
  private checking = false;
  private applying = false;
  private lastNotifiedBuild = 0;

  static getInstance(): AppUpdateService {
    if (!AppUpdateService.instance) AppUpdateService.instance = new AppUpdateService();
    return AppUpdateService.instance;
  }

  start(): void {
    if (this.started || process.env.NODE_ENV !== 'production') return;
    this.started = true;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleFocus);
    this.scheduleNextChecks();
    void this.checkNow(false);
  }

  stop(): void {
    if (!this.started) return;
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleFocus);
    if (this.interval !== null) window.clearInterval(this.interval);
    this.interval = null;
    this.started = false;
  }

  handleServiceWorkerUpdate(registration: ServiceWorkerRegistration): void {
    this.registration = registration;
    void this.checkNow(true);
  }

  async checkNow(refreshRemotePolicy: boolean = true): Promise<AppUpdateDecision | null> {
    if (this.checking || !navigator.onLine) return null;
    this.checking = true;

    try {
      if (refreshRemotePolicy) {
        await appBootstrapService.initialize();
        this.scheduleNextChecks();
      }

      const release = getAppConfig().release;
      if (release.updateMode === 'disabled') return null;

      const manifest = await this.fetchBuildManifest();
      const decision = evaluateAppUpdate(
        CURRENT_APP_BUILD.buildNumber,
        manifest.buildNumber,
        release
      );

      if (!decision.deploymentReady) {
        console.warn(
          `AppUpdateService: el build ${decision.availableBuild} fue configurado, `
          + `pero el servidor todavía publica ${manifest.buildNumber}.`
        );
        return decision;
      }

      if (!decision.available) return decision;
      const detail: AppUpdateEventDetail = {
        ...decision,
        manifest,
        message: release.message || 'Hay una nueva versión de la aplicación disponible.'
      };

      if (decision.mode === 'silent') {
        await this.applyUpdate(decision.availableBuild);
      } else if (this.lastNotifiedBuild !== decision.availableBuild || decision.mode === 'required') {
        this.lastNotifiedBuild = decision.availableBuild;
        window.dispatchEvent(new CustomEvent<AppUpdateEventDetail>('appUpdateAvailable', { detail }));
      }

      return decision;
    } catch (error) {
      console.warn('AppUpdateService: no se pudo comprobar la versión del build.', error);
      return null;
    } finally {
      this.checking = false;
    }
  }

  async applyUpdate(targetBuild?: number): Promise<void> {
    if (this.applying) return;
    this.applying = true;

    try {
      if (!('serviceWorker' in navigator)) {
        this.reloadPreservingSession(targetBuild);
        return;
      }

      const registration = this.registration
        || await navigator.serviceWorker.getRegistration()
        || await navigator.serviceWorker.ready;
      this.registration = registration;
      await registration.update();

      const waitingWorker = registration.waiting
        || await this.waitForInstalledWorker(registration, 15000);
      if (!waitingWorker) throw new Error('APP_UPDATE_WORKER_NOT_READY');

      await new Promise<void>((resolve) => {
        let completed = false;
        const finish = (): void => {
          if (completed) return;
          completed = true;
          navigator.serviceWorker.removeEventListener('controllerchange', finish);
          resolve();
        };
        navigator.serviceWorker.addEventListener('controllerchange', finish);
        waitingWorker.postMessage({ type: 'SKIP_WAITING' });
        window.setTimeout(finish, 10000);
      });

      this.reloadPreservingSession(targetBuild);
    } catch (error) {
      this.applying = false;
      window.dispatchEvent(new CustomEvent('appUpdateFailed', { detail: { error } }));
      throw error;
    }
  }

  private readonly handleVisibilityChange = (): void => {
    if (!document.hidden) void this.checkNow(true);
  };

  private readonly handleFocus = (): void => {
    void this.checkNow(true);
  };

  private scheduleNextChecks(): void {
    if (this.interval !== null) window.clearInterval(this.interval);
    const configuredMinutes = getAppConfig().release.checkIntervalMinutes;
    const minutes = Number.isFinite(configuredMinutes)
      ? Math.min(1440, Math.max(1, configuredMinutes))
      : 15;
    this.interval = window.setInterval(() => {
      void this.checkNow(true);
    }, minutes * 60 * 1000);
  }

  private async fetchBuildManifest(): Promise<AppBuildManifest> {
    const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const url = new URL(`${publicBase}/app-build.json`, window.location.href);
    url.searchParams.set('_', String(Date.now()));
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`APP_BUILD_MANIFEST_HTTP_${response.status}`);
    const manifest = await response.json();
    if (!isAppBuildManifest(manifest)) throw new Error('APP_BUILD_MANIFEST_INVALID');
    return manifest;
  }

  private waitForInstalledWorker(
    registration: ServiceWorkerRegistration,
    timeoutMs: number
  ): Promise<ServiceWorker | null> {
    if (registration.waiting) return Promise.resolve(registration.waiting);
    const worker = registration.installing;
    if (!worker) return Promise.resolve(null);

    return new Promise(resolve => {
      let resolved = false;
      const finish = (value: ServiceWorker | null): void => {
        if (resolved) return;
        resolved = true;
        worker.removeEventListener('statechange', handleStateChange);
        resolve(value);
      };
      const handleStateChange = (): void => {
        if (worker.state === 'installed') finish(registration.waiting || worker);
        if (worker.state === 'redundant') finish(null);
      };
      worker.addEventListener('statechange', handleStateChange);
      window.setTimeout(() => finish(registration.waiting), timeoutMs);
    });
  }

  private reloadPreservingSession(targetBuild?: number): void {
    if (targetBuild) sessionStorage.setItem('esimposioLastRequestedBuild', String(targetBuild));
    // Firebase usa persistencia LOCAL y Capacitor Storage no se limpia: la sesión
    // permanece intacta y el navegador vuelve a la misma ruta después del reload.
    window.location.reload();
  }
}

export const appUpdateService = AppUpdateService.getInstance();

declare global {
  interface WindowEventMap {
    appUpdateAvailable: CustomEvent<AppUpdateEventDetail>;
    appUpdateFailed: CustomEvent<{ error: unknown }>;
  }
}
